// Reusable image uploader — drag or click to upload, shows preview, returns URL.
import { useRef, useState } from "react";
import { Upload, Link2, X } from "lucide-react";
import api, { API_BASE, apiError } from "../../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/uploads/")) return `${BACKEND}${u}`;
  return u;
}

export default function ImageUploader({ value, onChange, onRemove }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlMode, setUrlMode] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  if (value) {
    return (
      <div className="relative group rounded-xl overflow-hidden border border-blush-100 bg-white">
        <img src={absUrl(value)} alt="" className="h-32 w-full object-cover" />
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 backdrop-blur border border-blush-100 text-blush-600 grid place-items-center opacity-0 group-hover:opacity-100 transition"
          aria-label="Remove"
        >
          <X size={13} strokeWidth={1.6} />
        </button>
      </div>
    );
  }

  return (
    <div className="h-32 rounded-xl border-2 border-dashed border-blush-200 bg-blush-50/30 grid place-items-center relative">
      {urlMode ? (
        <div className="w-full px-3">
          <input
            placeholder="Paste image URL"
            className="field text-xs !py-2"
            onChange={(e) => onChange(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setUrlMode(false)}
            className="mt-2 text-[10px] uppercase tracking-[0.18em] text-blush-600"
          >
            ← Upload instead
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="image-upload-input"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-blush-600 disabled:opacity-60"
          >
            <Upload size={13} strokeWidth={1.6} />
            {uploading ? "Uploading…" : "Upload image"}
          </button>
          <button
            type="button"
            onClick={() => setUrlMode(true)}
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-ink-500 hover:text-blush-600"
          >
            <Link2 size={11} strokeWidth={1.6} /> or paste URL
          </button>
        </div>
      )}
    </div>
  );
}

export { absUrl };
